import React, { useCallback, useMemo, useState } from 'react';

import { Stack } from '@mui/material';
import {
  DataGridPro,
  GridCallbackDetails,
  GridRow,
  GridRowParams,
  GridRowProps
} from '@mui/x-data-grid-pro';

import { GridFeatureMode } from '../../constants/enums';
import { usePaginationFromURL } from '../../hooks/usePaginationFromURL';
import { localStorageUtil } from '../../utils/localStorageUtil';
import ErrorBox from '../ErrorBox/ErrorBox';
import { Spinner } from '../ui/spinner';

import { DataTableProps } from './DataTable.types';

const DataTable = React.memo(
  ({
    columns,
    rows,
    headerText,
    loading,
    onNoData,
    onSelectionModelChange,
    selectionModel = undefined,
    rowIdIdentifier = '_id',
    autoHeight = true,
    defaultHiddenFields = [],
    localStorageColumnsKey,
    checkboxSelection = false,
    hideFooter = true,
    pagination = false,
    error = false,
    headerHeight = 56,
    initialSorting = undefined,
    allowEmptyRows = false,
    rowHeight = 52,
    onPageChange,
    onRowsPerPageChange,
    totalCount,
    currentPage,
    onSortModelChange,
    className,
    sortingMode = GridFeatureMode.CLIENT,
    filterMode = GridFeatureMode.CLIENT,
    onFilterModelChange,
    rowReordering,
    onRowOrderChange,
    onRowMouseEnter,
    getRowHeight,
    getRowClassName,
    columnVisibilityModel,
    onStateChange,
    onRowClick,
    pageSize
  }: DataTableProps) => {
    const { rows: pageSizeFromUrl } = usePaginationFromURL('');
    const [currentPageSize, setCurrentPageSize] = useState(pageSizeFromUrl);

    const getHiddenColumns = useCallback(() => {
      return defaultHiddenFields.map((hiddenField) => ({
        [hiddenField]: false
      }));
    }, [defaultHiddenFields]);

    const CustomRow = useCallback(
      (props: GridRowProps) => {
        const rowId = props.row?.[rowIdIdentifier] || props.row?.id;
        return <GridRow {...props} data-testid={rowId} />;
      },
      [rowIdIdentifier]
    );

    const handleMouseEnter = useCallback(
      (event: any) => {
        const rowElement = event.currentTarget.closest('.MuiDataGrid-row');
        const rowId = rowElement?.getAttribute('data-id');
        onRowMouseEnter?.(rowId);
      },
      [onRowMouseEnter]
    );

    const handlePageChange = useCallback(
      (page: number, details: GridCallbackDetails<any>) => {
        onPageChange && onPageChange(page);
      },
      [onPageChange]
    );

    const handleRowsPerPageChange = useCallback(
      (newPageSize: number) => {
        if (pageSize === undefined) {
          setCurrentPageSize(newPageSize);
        }
        onRowsPerPageChange && onRowsPerPageChange(newPageSize);
      },
      [onRowsPerPageChange, pageSize]
    );

    const initialState = useMemo(
      () => ({
        columns: {
          columnVisibilityModel: {
            ...Object.assign({}, ...getHiddenColumns()),
            ...(localStorageColumnsKey &&
              localStorageUtil.get(localStorageColumnsKey))
          }
        },
        sorting: initialSorting
      }),
      [getHiddenColumns, initialSorting, localStorageColumnsKey]
    );

    return (
      <div className={className}>
        {headerText && <h2>{headerText}</h2>}
        {error ? (
          <Stack
            mt={0}
            width="100%"
            height="calc(100vh - 150px)"
            alignItems="center"
            justifyContent="center"
          >
            <ErrorBox />
          </Stack>
        ) : (
          <>
            {loading && (
              <Spinner
                className="size-[80px]"
                color="rgb(67, 56, 202)"
                style={{
                  position: 'absolute',
                  top: '50%',
                  left: '50%'
                }}
              />
            )}
            {!loading && (rows?.length > 0 || allowEmptyRows) && (
              <DataGridPro
                selectionModel={selectionModel}
                columns={columns || []}
                rows={rows || []}
                page={currentPage}
                paginationMode="server"
                sortingMode={sortingMode}
                rowCount={totalCount || 0}
                onPageChange={handlePageChange}
                onPageSizeChange={handleRowsPerPageChange}
                onSortModelChange={onSortModelChange}
                pageSize={pageSize ?? currentPageSize}
                autoHeight={autoHeight}
                disableColumnResize={false}
                rowHeight={rowHeight}
                getRowHeight={getRowHeight}
                loading={loading}
                pagination={pagination}
                checkboxSelection={checkboxSelection}
                onSelectionModelChange={onSelectionModelChange}
                onColumnVisibilityModelChange={(data) =>
                  localStorageColumnsKey &&
                  localStorageUtil.set(localStorageColumnsKey, data)
                }
                columnVisibilityModel={columnVisibilityModel}
                initialState={initialState}
                getRowId={(row) => row[rowIdIdentifier]}
                hideFooter={hideFooter}
                filterMode={filterMode}
                onFilterModelChange={onFilterModelChange}
                rowReordering={rowReordering}
                onRowOrderChange={onRowOrderChange}
                onRowClick={(params: GridRowParams) => {
                  if (window.getSelection()?.toString()) {
                    return;
                  }

                  onRowClick?.(params);
                }}
                headerHeight={headerHeight}
                components={{
                  Row: CustomRow
                }}
                componentsProps={{
                  row: {
                    onMouseEnter: handleMouseEnter
                  }
                }}
                onStateChange={onStateChange}
                getRowClassName={getRowClassName}
              />
            )}
            {!loading && rows?.length === 0 && onNoData}
          </>
        )}
      </div>
    );
  }
);

export default DataTable;
